import { lineageHasSupersededInput } from "./epistemic-lineage.js";
import { assessClaim, } from "./epistemic.js";
/**
 * Research Mode Home v1.
 *
 * An inquiry is a question claim. Other claims join it only when recorded
 * lineage reaches that question, or when an in-inquiry claim explicitly cites
 * them. Sharing a Subject is not membership. Nothing here is a stance, and
 * nothing here is a suggestion.
 *
 * Extension point: if a future slice needs a durable inquiry id, replace
 * `questionClaims` as the root selector. Do not infer membership from
 * proximity, search, or embeddings.
 */
export const RESEARCH_NOT_A_POSITION = "Not an organisational position.";
export const RESEARCH_EMPTY = "No research inquiry is recorded for this Subject.";
export const RESEARCH_SUPERSEDED_INPUT = "An input used by this conclusion has been superseded.";
export const RESEARCH_IMPACT_HEADLINE = "Underlying knowledge changed";
export const RESEARCH_IMPACT_EXPLANATION = "This research conclusion depends on a claim that has since been superseded.";
export const RESEARCH_NO_LINEAGE = "This conclusion does not record what it follows from.";
export const RESEARCH_SOURCE_UNAVAILABLE = "This source is not available.";
export const RESEARCH_CONDITION_WITHHELD = "Condition not recorded. The consequence is withheld.";
export const RESEARCH_BEING_TESTED = "Being tested";
export const RESEARCH_SET_ASIDE = "Set aside";
export const RESEARCH_ASSUMPTION = "Assumption";
export const RESEARCH_FINDING = "Finding";
function byCreated(a, b) {
    return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : 1;
}
function indexClaims(claims, lineage, withheld) {
    const map = new Map();
    for (const row of claims)
        map.set(row.id, row);
    for (const row of lineage) {
        if (!withheld.has(row.id))
            map.set(row.id, row);
    }
    return map;
}
/**
 * Backward only, from a claim through `derived_from` claim ids, toward one
 * question. Evidence and fact refs are not followed. A shared finding does
 * not pull in other inquiries that also cite it: those inquiries are reached
 * only by walking their own lineage back to their own question.
 */
function reachesQuestion(claim, questionId, byId, seen) {
    if (claim.id === questionId)
        return true;
    if (seen.has(claim.id))
        return false;
    seen.add(claim.id);
    for (const ref of claim.derivedFrom) {
        if (ref.kind !== "claim")
            continue;
        if (ref.id === questionId)
            return true;
        const next = byId.get(ref.id);
        if (next && reachesQuestion(next, questionId, byId, seen))
            return true;
    }
    return false;
}
function selfRejected(claim) {
    const stance = assessClaim(claim).selfStance?.stance ?? null;
    return stance === "rejected";
}
function setAside(claim) {
    return claim.supersededAt !== null || selfRejected(claim);
}
function inheritedCondition(claim, byId, depth = 0) {
    if (claim.condition && claim.condition.trim().length > 0)
        return claim.condition;
    if (depth >= 4)
        return null;
    for (const ref of claim.derivedFrom) {
        if (ref.kind !== "claim")
            continue;
        const next = byId.get(ref.id);
        if (!next)
            continue;
        const found = inheritedCondition(next, byId, depth + 1);
        if (found)
            return found;
    }
    return null;
}
function isConditional(claim, byId) {
    return assessClaim(claim, (id) => byId.get(id) ?? null).conditional;
}
function qualifierFor(claim, conditional) {
    if (claim.inferenceMode === "hypothesis") {
        return setAside(claim) ? RESEARCH_SET_ASIDE : RESEARCH_BEING_TESTED;
    }
    if (claim.inferenceMode === "synthesis")
        return "Research conclusion";
    if (claim.inferenceMode === "implication")
        return "Derived result";
    if (claim.claimType === "assumption")
        return RESEARCH_ASSUMPTION;
    if (claim.claimType === "question")
        return "Open question";
    if (claim.claimType === "scenario" || claim.inferenceMode === "scenario" || conditional) {
        return "Conditional";
    }
    const assessment = assessClaim(claim);
    if (assessment.verification === "source-only")
        return "Source only";
    if (assessment.verification === "unverified")
        return "Not verified";
    return RESEARCH_FINDING;
}
function toItem(claim, byId) {
    const conditional = isConditional(claim, byId);
    return {
        id: claim.id,
        proposition: claim.proposition,
        qualifier: qualifierFor(claim, conditional),
        verification: assessClaim(claim, (id) => byId.get(id) ?? null).verification,
        sourceSubjectId: claim.subjectId,
        copied: false,
        setAside: claim.inferenceMode === "hypothesis" ? setAside(claim) : false,
        conditional,
    };
}
function frameFor(claim, byId) {
    const condition = inheritedCondition(claim, byId);
    const withheld = condition === null;
    return {
        id: claim.id,
        condition,
        withheld,
        consequence: withheld ? null : claim.proposition,
        qualifier: "Conditional",
    };
}
function claimRefs(claim) {
    return claim.derivedFrom.filter((ref) => ref.kind === "claim").map((ref) => ref.id);
}
/** Superseded claim inputs in the backward closure. Latest change, then id. */
function changedInputId(claim, byId) {
    const found = [];
    const seen = new Set([claim.id]);
    const queue = [...claimRefs(claim)];
    while (queue.length > 0) {
        const id = queue.shift();
        if (!id || seen.has(id))
            continue;
        seen.add(id);
        const next = byId.get(id);
        if (!next)
            continue;
        if (next.supersededAt)
            found.push(next);
        queue.push(...claimRefs(next));
    }
    found.sort((a, b) => {
        const at = (b.supersededAt ?? "").localeCompare(a.supersededAt ?? "");
        return at !== 0 ? at : a.id < b.id ? -1 : 1;
    });
    return found[0]?.id ?? null;
}
function whySteps(conclusion, questionId, byId, withheld, inquiryIds) {
    const steps = [];
    let current = conclusion;
    const seen = new Set([conclusion.id, questionId]);
    while (steps.length < 3) {
        const nextId = claimRefs(current).find((id) => !seen.has(id));
        if (!nextId)
            break;
        seen.add(nextId);
        if (withheld.has(nextId) || !byId.has(nextId)) {
            steps.push({
                id: null,
                proposition: null,
                qualifier: RESEARCH_SOURCE_UNAVAILABLE,
                unavailable: true,
            });
            break;
        }
        const next = byId.get(nextId);
        steps.push({
            id: next.id,
            proposition: next.proposition,
            qualifier: qualifierFor(next, isConditional(next, byId)),
            unavailable: false,
        });
        // A cited finding is shown. Its own inputs are not this inquiry's reasoning.
        if (!inquiryIds.has(next.id))
            break;
        current = next;
    }
    return steps;
}
function contradictsOn(claim, byId, withheld, inquiryIds, seen) {
    if (seen.has(claim.id))
        return [];
    seen.add(claim.id);
    const own = claim.evidence
        .filter((row) => row.relation === "contradicts")
        .map((row) => ({
        onClaimId: claim.id,
        onProposition: claim.proposition,
        source: row.source,
        asOf: row.asOf,
        authority: row.authority,
        excerpt: row.excerpt,
    }));
    if (!inquiryIds.has(claim.id))
        return own;
    const nested = claimRefs(claim).flatMap((id) => {
        if (withheld.has(id))
            return [];
        const next = byId.get(id);
        return next ? contradictsOn(next, byId, withheld, inquiryIds, seen) : [];
    });
    return [...own, ...nested];
}
function assumptionsIn(claim, byId, withheld, inquiryIds, seen) {
    if (seen.has(claim.id))
        return [];
    seen.add(claim.id);
    if (!inquiryIds.has(claim.id)) {
        return claim.claimType === "assumption" ? [claim] : [];
    }
    const nested = claimRefs(claim).flatMap((id) => {
        if (withheld.has(id))
            return [];
        const next = byId.get(id);
        return next ? assumptionsIn(next, byId, withheld, inquiryIds, seen) : [];
    });
    return claim.claimType === "assumption" ? [claim, ...nested] : nested;
}
export function buildResearchHome(input) {
    const withheld = new Set(input.withheldClaimIds ?? []);
    const byId = indexClaims(input.claims, input.lineage ?? [], withheld);
    const questions = input.claims
        .filter((row) => row.subjectId === input.subjectId &&
        row.claimType === "question" &&
        row.supersededAt === null)
        .sort(byCreated);
    if (questions.length === 0)
        return { status: "empty", message: RESEARCH_EMPTY };
    const requested = input.activeQuestionId
        ? questions.find((row) => row.id === input.activeQuestionId)
        : undefined;
    const active = requested ?? questions[0];
    const inInquiry = input.claims.filter((row) => reachesQuestion(row, active.id, byId, new Set()));
    const inquiryIds = new Set(inInquiry.map((row) => row.id));
    const usedIds = new Set();
    for (const row of inInquiry) {
        for (const id of claimRefs(row)) {
            if (id !== active.id && !withheld.has(id))
                usedIds.add(id);
        }
    }
    const syntheses = inInquiry
        .filter((row) => row.inferenceMode === "synthesis" && row.supersededAt === null)
        .sort(byCreated);
    const conclusionClaim = syntheses.length > 0 ? syntheses[syntheses.length - 1] : null;
    const hypotheses = inInquiry
        .filter((row) => row.inferenceMode === "hypothesis")
        .sort(byCreated)
        .map((row) => toItem(row, byId));
    if (input.focusedHypothesisId) {
        const idx = hypotheses.findIndex((row) => row.id === input.focusedHypothesisId);
        if (idx > 0) {
            const [focused] = hypotheses.splice(idx, 1);
            hypotheses.unshift(focused);
        }
    }
    const usedClaims = [...usedIds]
        .map((id) => byId.get(id))
        .filter((row) => row !== undefined)
        .sort(byCreated);
    const findings = usedClaims
        .filter((row) => row.claimType !== "assumption" &&
        row.claimType !== "question" &&
        row.claimType !== "scenario" &&
        row.inferenceMode !== "hypothesis" &&
        row.inferenceMode !== "synthesis" &&
        row.inferenceMode !== "scenario" &&
        row.inferenceMode !== "implication" &&
        !isConditional(row, byId))
        .map((row) => toItem(row, byId));
    const assumptionClaims = usedClaims.filter((row) => row.claimType === "assumption");
    const nearAssumptions = conclusionClaim
        ? assumptionsIn(conclusionClaim, byId, withheld, inquiryIds, new Set())
        : [];
    const assumptionIds = new Set();
    const assumptions = [...nearAssumptions, ...assumptionClaims]
        .filter((row) => {
        if (assumptionIds.has(row.id))
            return false;
        assumptionIds.add(row.id);
        return true;
    })
        .map((row) => toItem(row, byId));
    const scenarioClaims = [
        ...inInquiry.filter((row) => row.id !== conclusionClaim?.id &&
            (row.claimType === "scenario" ||
                row.inferenceMode === "scenario" ||
                (isConditional(row, byId) && row.inferenceMode !== "hypothesis"))),
        ...usedClaims.filter((row) => row.claimType === "scenario" ||
            row.inferenceMode === "scenario" ||
            isConditional(row, byId)),
    ];
    const scenarioIds = new Set();
    const scenarios = scenarioClaims
        .filter((row) => {
        if (scenarioIds.has(row.id))
            return false;
        if (row.inferenceMode === "hypothesis")
            return false;
        if (!isConditional(row, byId) &&
            row.claimType !== "scenario" &&
            row.inferenceMode !== "scenario") {
            return false;
        }
        scenarioIds.add(row.id);
        return true;
    })
        .map((row) => frameFor(row, byId));
    const unknowns = usedClaims
        .filter((row) => row.claimType === "question" && row.id !== active.id)
        .map((row) => toItem(row, byId));
    const why = conclusionClaim
        ? whySteps(conclusionClaim, active.id, byId, withheld, inquiryIds)
        : [];
    return {
        status: "inquiry",
        inquiries: questions.map((row) => ({ id: row.id, question: row.proposition })),
        activeQuestionId: active.id,
        question: active.proposition,
        conclusion: conclusionClaim
            ? {
                id: conclusionClaim.id,
                proposition: conclusionClaim.proposition,
                qualifier: "Research conclusion",
                notOrganisationalPosition: RESEARCH_NOT_A_POSITION,
                supersededInput: lineageHasSupersededInput(conclusionClaim, (id) => byId.get(id) ?? null),
            }
            : null,
        why,
        whyMissing: conclusionClaim !== null && why.length === 0,
        contradicts: conclusionClaim
            ? contradictsOn(conclusionClaim, byId, withheld, inquiryIds, new Set())
            : [],
        assumptions,
        known: {
            findings: findings.filter((row) => !row.setAside),
            assumptions,
            scenarios: scenarios.filter((row) => usedIds.has(row.id) || scenarioIds.has(row.id)),
        },
        hypotheses,
        unknowns,
        scenarios,
        impact: conclusionClaim
            ? (() => {
                const changedClaimId = changedInputId(conclusionClaim, byId);
                return changedClaimId
                    ? {
                        changedClaimId,
                        headline: RESEARCH_IMPACT_HEADLINE,
                        explanation: RESEARCH_IMPACT_EXPLANATION,
                    }
                    : null;
            })()
            : null,
    };
}
/** Ask may read the same inquiry. The block never confers a stance. */
export function researchInquiryAskBlock(home) {
    if (home.status !== "inquiry")
        return null;
    const lines = [
        "## RESEARCH INQUIRY (not an organisational position)",
        "This is the inquiry recorded on the Subject. Do not treat it as adopted.",
        `Question: ${home.question}`,
    ];
    if (home.conclusion) {
        lines.push(`Research conclusion: ${home.conclusion.proposition}`);
        lines.push(RESEARCH_NOT_A_POSITION);
        if (home.conclusion.supersededInput)
            lines.push(RESEARCH_SUPERSEDED_INPUT);
    }
    else {
        lines.push("No research conclusion is recorded.");
    }
    const focused = home.hypotheses[0];
    if (focused) {
        lines.push(focused.setAside
            ? `${RESEARCH_SET_ASIDE}: ${focused.proposition}`
            : `${RESEARCH_BEING_TESTED}: ${focused.proposition}`);
    }
    return lines.join("\n");
}
/**
 * Durable dependency is this payload, written only when a person confirms.
 * Staging a claim id must not call this.
 */
export function confirmResearchReasoning(input) {
    const ids = [input.questionId, ...input.usedClaimIds.filter((id) => id !== input.questionId)];
    const unique = [...new Set(ids)];
    return {
        proposition: input.proposition.trim(),
        claimType: "derived",
        attributedTo: null,
        factKey: null,
        factCitationId: null,
        verification: "unverified",
        inferenceMode: input.mode,
        condition: input.mode === "scenario" ? input.condition?.trim() || null : null,
        derivedFrom: unique.map((id) => ({ kind: "claim", id })),
        derivationNote: null,
        note: null,
        evidence: [],
        stances: [],
    };
}
/** Inspect / open / stage. No lineage write. */
export function stageClaimForUse(staged, claimId) {
    return staged.includes(claimId) ? [...staged] : [...staged, claimId];
}
export function retainViewableLineage(lineage, viewableSubjectIds) {
    return lineage.filter((row) => viewableSubjectIds.has(row.subjectId));
}
//# sourceMappingURL=research-home.js.map